import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { comicTags } from "@/mocks/comics";

const navLinks = [
  { label: "首页", path: "/" },
  { label: "全部漫画", path: "/category" },
  { label: "题材专题", path: "/topics" },
  { label: "关于我们", path: "/about" },
  { label: "联系我们", path: "/contact" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [tagOpen, setTagOpen] = useState(false);
  const [query, setQuery] = useState("");

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/category?q=${encodeURIComponent(query.trim())}`);
      setQuery("");
      setOpen(false);
    }
  };

  const goTo = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  const goTag = (tag: string) => {
    setOpen(false);
    setTagOpen(false);
    navigate(`/category?tag=${encodeURIComponent(tag)}`);
  };

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  return (
    <header className="sticky top-0 z-50 w-full border-b border-background-200/80 bg-background-50/95 backdrop-blur">
      <div className="container-x flex h-16 items-center justify-between gap-4 md:h-18">
        {/* 左：Logo + 站内导航 */}
        <div className="flex items-center gap-6">
          <a
            href="/"
            className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              goTo("/");
            }}
          >
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-brush-4-line text-lg" aria-hidden="true"></i>
            </span>
            <span className="font-heading text-xl font-bold tracking-wide text-foreground-950">
              古风漫画网
            </span>
          </a>

          <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
            {navLinks.slice(0, 2).map((item) => (
              <button
                key={item.path}
                onClick={() => goTo(item.path)}
                className={`rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  isActive(item.path)
                    ? "text-primary-600"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {item.label}
              </button>
            ))}

            {/* 题材下拉 */}
            <div className="relative">
              <button
                onClick={() => setTagOpen((v) => !v)}
                className={`flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  tagOpen || isActive("/category")
                    ? "text-primary-600"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
                aria-haspopup="menu"
                aria-expanded={tagOpen}
              >
                题材分类
                <i className={`ri-arrow-down-s-line transition-transform ${tagOpen ? "rotate-180" : ""}`}></i>
              </button>
              {tagOpen && (
                <div className="absolute left-0 top-full mt-2 w-48 rounded-lg border border-background-300/70 bg-background-50 p-2 shadow-lg">
                  <p className="px-3 py-1.5 text-xs font-semibold text-foreground-500">按题材浏览</p>
                  {comicTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => goTag(tag)}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 cursor-pointer"
                    >
                      <i className="ri-arrow-right-s-line text-foreground-400"></i>
                      {tag}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {navLinks.slice(2).map((item) => (
              <button
                key={item.path}
                onClick={() => goTo(item.path)}
                className={`rounded-md px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  isActive(item.path)
                    ? "text-primary-600"
                    : "text-foreground-700 hover:text-primary-600"
                }`}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </div>

        {/* 右：搜索 + 移动端菜单按钮 */}
        <div className="flex items-center gap-2">
          <form onSubmit={onSearch} className="hidden items-center md:flex" role="search">
            <div className="flex items-center rounded-full border border-background-300 bg-background-100 pl-3 pr-1 py-1 focus-within:border-primary-400">
              <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索漫画 / 作者"
                aria-label="搜索漫画"
                className="w-40 bg-transparent px-2 text-sm text-foreground-800 outline-none placeholder:text-foreground-400"
              />
              <button
                type="submit"
                className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                aria-label="搜索"
              >
                <i className="ri-arrow-right-line text-xs"></i>
              </button>
            </div>
          </form>

          <button
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-background-300 text-foreground-700 md:hidden cursor-pointer"
            aria-label="打开菜单"
            aria-expanded={open}
          >
            <i className={open ? "ri-close-line text-xl" : "ri-menu-line text-xl"}></i>
          </button>
        </div>
      </div>

      {/* 移动端菜单 */}
      {open && (
        <div className="border-t border-background-200 bg-background-50 px-4 py-4 md:hidden">
          <form onSubmit={onSearch} className="mb-4 flex items-center gap-2" role="search">
            <div className="flex flex-1 items-center rounded-full border border-background-300 bg-background-100 px-3 py-2">
              <i className="ri-search-line text-sm text-foreground-500" aria-hidden="true"></i>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜索漫画 / 作者"
                aria-label="搜索漫画"
                className="w-full bg-transparent px-2 text-sm text-foreground-800 outline-none"
              />
            </div>
            <button
              type="submit"
              className="rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 cursor-pointer"
            >
              搜索
            </button>
          </form>
          <nav className="grid grid-cols-2 gap-2" aria-label="移动端导航">
            {navLinks.map((item) => (
              <button
                key={item.path}
                onClick={() => goTo(item.path)}
                className={`rounded-md px-4 py-2.5 text-left text-sm font-medium ${
                  isActive(item.path) ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-800"
                } cursor-pointer`}
              >
                {item.label}
              </button>
            ))}
          </nav>
          <p className="mt-4 px-1 text-xs font-semibold text-foreground-500">题材直达</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {comicTags.map((tag) => (
              <button
                key={tag}
                onClick={() => goTag(tag)}
                className="rounded-full border border-background-300 px-3 py-1.5 text-xs text-foreground-700 cursor-pointer"
              >
                {tag}
              </button>
            ))}
          </div>
        </div>
      )}
    </header>
  );
}